import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCityContext } from '../../context/CityContext';
import Button from '../../components/UI/Button';
import './AdminConfig.css';

const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'accent', label: 'Accent' },
  { key: 'buttonPrimary', label: 'Button' },
  { key: 'cardAccent', label: 'Card Accent' },
];

export default function AdminConfig() {
  const { cityList, deactivateCity, updateCityConfig } = useCityContext();
  const navigate = useNavigate();

  const [selectedSlug, setSelectedSlug] = useState(cityList[0]?.slug || '');
  const [draftColors, setDraftColors] = useState({});
  const [draftName, setDraftName] = useState('');
  const [draftTagline, setDraftTagline] = useState('');
  const [saved, setSaved] = useState(false);

  const selectedCity = cityList.find((c) => c.slug === selectedSlug);

  useEffect(() => {
    deactivateCity();
    document.title = 'Admin Config — Notify Cities';
  }, [deactivateCity]);

  useEffect(() => {
    if (selectedCity) {
      setDraftColors({ ...selectedCity.colors });
      setDraftName(selectedCity.name);
      setDraftTagline(selectedCity.tagline);
      setSaved(false);
    }
  }, [selectedSlug, selectedCity]);

  function handleColorChange(key, value) {
    const updated = { ...draftColors, [key]: value };
    // Keep derived fields in sync
    if (key === 'primary') {
      updated.headerBg = value;
      updated.heroBg = value;
    }
    if (key === 'buttonPrimary') {
      updated.buttonPrimary = value;
    }
    if (key === 'secondary') {
      updated.tagText = value;
    }
    setDraftColors(updated);
  }

  function handleSave() {
    updateCityConfig(selectedSlug, {
      name: draftName,
      tagline: draftTagline,
      colors: draftColors,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function handleReset() {
    if (selectedCity) {
      setDraftColors({ ...selectedCity.colors });
      setDraftName(selectedCity.name);
      setDraftTagline(selectedCity.tagline);
    }
  }

  return (
    <div className="admin">
      {/* Header */}
      <div className="admin-topbar">
        <div className="admin-topbar-inner">
          <div className="admin-topbar-left">
            <Link to="/" className="admin-back">← Back to Cities</Link>
            <div className="admin-topbar-title">
              <span className="admin-topbar-icon">⚙</span>
              Admin Config Panel
            </div>
          </div>
          <div className="admin-topbar-right">
            <Button variant="ghost" size="sm" onClick={handleReset}>Reset</Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              {saved ? '✓ Saved!' : 'Save Changes'}
            </Button>
            {selectedSlug && (
              <Button variant="secondary" size="sm" onClick={() => navigate(`/${selectedSlug}`)}>
                Preview →
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="admin-body">
        {/* Sidebar — city list */}
        <aside className="admin-sidebar">
          <div className="admin-sidebar-heading">Cities</div>
          {cityList.map((city) => (
            <button
              key={city.slug}
              className={`admin-city-btn${selectedSlug === city.slug ? ' active' : ''}`}
              onClick={() => setSelectedSlug(city.slug)}
            >
              <div
                className="admin-city-mark"
                style={{ background: city.colors.primary }}
              >
                {city.name.charAt(0)}
              </div>
              <div>
                <div className="admin-city-name">{city.name}</div>
                <div className="admin-city-state">{city.state}</div>
              </div>
            </button>
          ))}
          <div className="admin-sidebar-hint">
            More cities can be added by extending <code>src/config/cities.js</code>
          </div>
        </aside>

        {/* Main editor */}
        {selectedCity && (
          <div className="admin-editor">
            {/* Live Preview Bar */}
            <div
              className="admin-preview-bar"
              style={{
                background: draftColors.primary || selectedCity.colors.primary,
                color: '#fff',
              }}
            >
              <div className="admin-preview-bar-inner">
                <div className="admin-preview-logo">
                  <div
                    className="admin-preview-mark"
                    style={{ background: draftColors.buttonPrimary || selectedCity.colors.buttonPrimary }}
                  >
                    {draftName.charAt(0)}
                  </div>
                  <span style={{ fontWeight: 700 }}>Notify {draftName}</span>
                </div>
                <div className="admin-preview-nav">
                  <span>Browse Alerts</span>
                  <span>My Notifications</span>
                </div>
              </div>
            </div>

            <div className="admin-preview-hero" style={{ background: draftColors.primary }}>
              <div
                className="admin-preview-hero-mark"
                style={{
                  background: draftColors.buttonPrimary,
                  color: draftColors.buttonPrimaryText || '#fff',
                }}
              >
                {draftName.charAt(0)}
              </div>
              <div>
                <div className="admin-preview-hero-title" style={{ color: '#fff' }}>
                  Notify {draftName}
                </div>
                <div className="admin-preview-hero-sub" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {draftTagline}
                </div>
              </div>
            </div>

            <div className="admin-preview-cards">
              {selectedCity.topics.slice(0, 3).map((t) => (
                <div
                  key={t.id}
                  className="admin-preview-card"
                  style={{ borderTopColor: draftColors.cardAccent }}
                >
                  <div className="admin-preview-card-icon">{t.icon}</div>
                  <div className="admin-preview-card-name">{t.name}</div>
                  <div
                    className="admin-preview-card-btn"
                    style={{
                      background: draftColors.buttonPrimary,
                      color: draftColors.buttonPrimaryText || '#fff',
                    }}
                  >
                    Subscribe
                  </div>
                </div>
              ))}
            </div>

            {/* Editor fields */}
            <div className="admin-fields">
              <div className="admin-section">
                <div className="admin-section-title">City Info</div>
                <div className="admin-field-row">
                  <div className="admin-field">
                    <label className="admin-label">City Name</label>
                    <input
                      className="admin-input"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                    />
                  </div>
                  <div className="admin-field admin-field--wide">
                    <label className="admin-label">Tagline</label>
                    <input
                      className="admin-input"
                      value={draftTagline}
                      onChange={(e) => setDraftTagline(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="admin-section">
                <div className="admin-section-title">Theme Colors</div>
                <p className="admin-section-sub">
                  Changes apply to the live preview above instantly.
                </p>
                <div className="admin-color-grid">
                  {COLOR_FIELDS.map(({ key, label }) => (
                    <div key={key} className="admin-color-field">
                      <label className="admin-label">{label}</label>
                      <div className="admin-color-input-row">
                        <input
                          type="color"
                          className="admin-color-swatch"
                          value={draftColors[key] || '#000000'}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                        />
                        <input
                          className="admin-input admin-input--hex"
                          value={draftColors[key] || ''}
                          onChange={(e) => handleColorChange(key, e.target.value)}
                          placeholder="#000000"
                          maxLength={7}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-section">
                <div className="admin-section-title">Topics</div>
                <p className="admin-section-sub">
                  Topics are configured in <code>src/config/cities.js</code>. Each topic can specify an icon, category, description, and whether it requires a street address.
                </p>
                <div className="admin-topics-list">
                  {selectedCity.topics.map((t) => (
                    <div key={t.id} className="admin-topic-row">
                      <span className="admin-topic-icon">{t.icon}</span>
                      <span className="admin-topic-name">{t.name}</span>
                      <span className="admin-topic-cat">{t.category}</span>
                      {t.requiresAddress && (
                        <span className="admin-topic-tag">📍 Requires address</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-section admin-section--code">
                <div className="admin-section-title">Config Snippet</div>
                <p className="admin-section-sub">
                  The complete city definition lives in a single config object. Add a new entry to <code>src/config/cities.js</code> to add a new city — no other code changes needed.
                </p>
                <pre className="admin-code-block">{`{
  slug: '${selectedSlug}',
  name: '${draftName}',
  state: '${selectedCity.state}',
  tagline: '${draftTagline}',
  colors: {
    primary: '${draftColors.primary || selectedCity.colors.primary}',
    secondary: '${draftColors.secondary || selectedCity.colors.secondary}',
    accent: '${draftColors.accent || selectedCity.colors.accent}',
    buttonPrimary: '${draftColors.buttonPrimary || selectedCity.colors.buttonPrimary}',
    ...
  },
  topics: [ /* topic array */ ]
}`}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
